import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import WikiPaywall from "../components/WikiPaywall";
import { BookOpen, Lock, ArrowRight } from "lucide-react";

export default function Wiki() {
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);

  const hasAccess = !!user?.wiki_access;

  useEffect(() => {
    if (!hasAccess) {
      setLoading(false);
      return;
    }
    setLoading(true);
    api.get(`/wiki?lang=${lang}`).then((r) => {
      setDomains(r.data.domains);
      setLoading(false);
    });
  }, [lang, hasAccess]);

  if (!hasAccess) return <WikiPaywall />;

  if (loading)
    return <div className="min-h-[60vh] grid place-items-center text-slate-400 font-mono text-sm">{t("wiki.loading")}</div>;

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid="wiki-page">
      <div className="max-w-3xl">
        <div className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-teal-700 mb-3">
          <BookOpen size={14} /> {t("wiki.kicker")}
          <span className="inline-flex items-center gap-1 text-slate-400 border border-slate-200 rounded-full px-2 py-0.5">
            <Lock size={11} /> {t("wiki.membersOnly")}
          </span>
        </div>
        <h1 className="font-serif font-bold text-4xl sm:text-5xl tracking-tight text-slate-900">{t("wiki.title")}</h1>
        <p className="mt-4 text-base text-slate-500 leading-relaxed">{t("wiki.subtitle")}</p>
      </div>

      <div className="mt-10 font-mono text-xs uppercase tracking-widest text-slate-400">{t("wiki.domainsLabel")}</div>
      <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {domains.map((d, i) => (
          <motion.div
            key={d.number}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: i * 0.04 }}
          >
            <Link
              to={d.articles[0] ? `/wiki/${d.articles[0].slug}` : "#"}
              data-testid={`wiki-domain-card-${d.number}`}
              className="group block h-full rounded-2xl border bg-white p-6 transition-all hover:-translate-y-1"
              style={{ borderColor: `${d.color}44`, background: `linear-gradient(145deg, #fff, ${d.soft || "#fff"})` }}
            >
              <div className="flex items-baseline justify-between">
                <span className="font-serif font-medium text-4xl" style={{ color: d.color }}>{d.number}</span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
                  {d.articles.length} {t("wiki.entries")}
                </span>
              </div>
              <h2 className="mt-5 font-serif font-bold text-xl text-slate-900 leading-snug">{d.title}</h2>
              <p className="mt-1.5 text-sm text-slate-500">{d.subtitle}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: d.color }}>
                {t("wiki.openDomain")} <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
